'use client';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import SortableItem from '@/components/exercise/sortable-item';
import type { ContentItem } from '@/types';

export default function SortableList({
  answers,
  onDragEnd,
}: {
  answers: ContentItem[];
  onDragEnd: (sequence: number[]) => void;
}) {
  const [items, setItems] = useState(answers);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms delay for touch
        tolerance: 5, // 5px tolerance during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex(({ id }) => id === active.id);
        const newIndex = items.findIndex(({ id }) => id === over.id);
        const newArray = arrayMove(items, oldIndex, newIndex);
        // Update parent component about sequence change e.g. [2, 0, 1, 3]
        onDragEnd(newArray.map(({ index }) => index));
        // Update current state to reflect new sequence
        return newArray;
      });
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map(({ id, content }) => (
          <SortableItem key={id} id={id} content={content} />
        ))}
      </SortableContext>
    </DndContext>
  );
}
